// Le filtre qu'un abonné pose sur UN site qu'il suit (issue #191, décision du
// 2026-10-16).
//
// CE QUE ÇA REMPLACE. Le filtre vivait dans la config de la source, choisi par
// nous une fois pour tous : Engie lu en Belgique, Elia en français, UCB avec
// `country: Belgium`. Un abonné n'y pouvait rien, et un site d'annonces comme le
// Forem (des milliers d'offres) ne se suit pas sans choisir ce qu'on y veut.
//
// LE PRINCIPE. La source collecte tout ; le filtre se pose sur l'abonnement
// (`source_subscriptions.filtre`, JSON), vide veut dire tout. Il porte deux
// dimensions : la ZONE (une région, ou une province belge) et la LANGUE DE
// L'ANNONCE. Il borne ce que le compte voit ET ce pour quoi il paie du scoring,
// par un seul fragment SQL (`suitLOffre`, src/services/abonnements.ts) qui
// appelle `passe_filtre`, la fonction SQLite enregistrée ici à l'ouverture de
// la base : calculée à la lecture, elle ne se périme jamais quand un lieu ou
// une description arrive plus tard.
//
// UNE OFFRE DONT ON NE SAIT RIEN PASSE TOUJOURS, comme pour les régions des
// Réglages : un lieu inconnu, une langue qu'on ne reconnaît pas, une province
// qu'on ne lit pas alors que le filtre en nomme une de la même région. On
// n'écarte que ce qu'on sait être ailleurs.
//
// Module pur (aucun import de la base) : db.ts l'importe pour enregistrer la
// fonction, il ne doit pas l'importer en retour.

import { REGIONS, regionDe, type Region } from './regions'

export const PROVINCES = [
  'brabant-wallon',
  'hainaut',
  'liege',
  'luxembourg-be',
  'namur',
  'anvers',
  'brabant-flamand',
  'flandre-occidentale',
  'flandre-orientale',
  'limbourg',
] as const
export type Province = (typeof PROVINCES)[number]

/** La région de chaque province : un filtre qui nomme la province sait aussi
 *  de quelle région il parle quand la province d'une offre est illisible. */
export const REGION_DE_PROVINCE: Record<Province, Region> = {
  'brabant-wallon': 'wallonie',
  hainaut: 'wallonie',
  liege: 'wallonie',
  'luxembourg-be': 'wallonie',
  namur: 'wallonie',
  anvers: 'flandre',
  'brabant-flamand': 'flandre',
  'flandre-occidentale': 'flandre',
  'flandre-orientale': 'flandre',
  limbourg: 'flandre',
}

/** Les pays de l'étranger, comme les provinces le sont de la Wallonie et de la
 *  Flandre (issue #194) : Radar collecte la France, les Pays-Bas, l'Allemagne
 *  et la Suisse chez les employeurs qui y publient (Engie, UCB, J&J…).
 *  `autre-pays` range un lieu étranger reconnu ailleurs (Istanbul chez
 *  Accenture Suisse, Houston chez GSK) : il ne passe pas un filtre qui nomme
 *  des pays, puisqu'on SAIT qu'il est ailleurs. */
export const PAYS = ['france', 'pays-bas', 'allemagne', 'suisse', 'autre-pays'] as const
export type Pays = (typeof PAYS)[number]

/** Le découpage d'une région : provinces belges, pays de l'étranger. */
export type SousZone = Province | Pays
export const SOUS_ZONES: readonly SousZone[] = [...PROVINCES, ...PAYS]
export const REGION_DE_SOUS_ZONE: Record<SousZone, Region> = {
  ...REGION_DE_PROVINCE,
  ...(Object.fromEntries(PAYS.map((p) => [p, 'etranger'])) as Record<Pays, Region>),
}

/** Une zone de filtre : une région entière, une province belge ou un pays. */
export type Zone = Region | SousZone
export const ZONES: readonly Zone[] = [...REGIONS, ...SOUS_ZONES]

export const LANGUES_ANNONCE = ['fr', 'nl', 'en', 'de'] as const
export type LangueAnnonce = (typeof LANGUES_ANNONCE)[number]

export interface FiltreAbonnement {
  zones?: Zone[]
  langues?: LangueAnnonce[]
}

const sansAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
const mots = (liste: string[]) =>
  new RegExp(`(^|[^a-z])(${liste.map((m) => m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})($|[^a-z])`)

/** Les noms qui disent une province, quand le lieu n'a pas de code postal.
 *  Les provinces wallonnes sont lues AVANT les flamandes : « louvain » est
 *  dans « louvain-la-neuve ». Une commune qui manque se lit comme inconnue et
 *  passe le filtre : l'ajouter ici. */
const NOMS_PROVINCES: [Province, RegExp][] = [
  ['brabant-wallon', mots([
    'brabant wallon', 'waals-brabant', 'wavre', 'waver', 'louvain-la-neuve', 'ottignies', "braine-l'alleud",
    "braine l'alleud", 'waterloo', 'nivelles', 'nijvel', 'mont-saint-guibert', 'rixensart', 'genval', 'la hulpe',
    'lasne', 'tubize', 'rebecq', 'jodoigne', 'perwez', 'court-saint-etienne',
  ])],
  ['hainaut', mots([
    'hainaut', 'henegouwen', 'charleroi', 'mons', 'tournai', 'doornik', 'mouscron', 'moeskroen', 'la louviere',
    'gosselies', 'ath', 'soignies', 'enghien', 'braine-le-comte', 'fleurus', 'binche', 'peruwelz', 'comines',
    'lessines', 'frameries', 'saint-ghislain', 'seneffe', 'manage', 'courcelles', 'thuin', 'chimay', 'chatelet',
    'jumet', 'marcinelle',
  ])],
  ['namur', mots([
    'namur', 'namen', 'gembloux', 'isnes', 'dinant', 'ciney', 'sambreville', 'andenne', 'jambes', 'couvin',
    'philippeville',
  ])],
  ['liege', mots([
    'liege', 'luik', 'verviers', 'seraing', 'herstal', 'huy', 'hoei', 'spa', 'eupen', 'malmedy', 'saint-vith',
    'sankt vith', 'waremme', 'ans', 'flemalle', 'chaudfontaine', 'stavelot', 'aywaille', 'sprimont', 'esneux',
    'fleron', 'soumagne', 'vise', 'oupeye', 'grace-hollogne', 'hannut', 'wanze', 'amay', 'angleur',
  ])],
  ['luxembourg-be', mots([
    'arlon', 'aarlen', 'marche-en-famenne', 'bastogne', 'libramont', 'neufchateau', 'virton', 'aubange', 'athus',
    'durbuy', 'houffalize', 'vielsalm', 'luxembourg',
  ])],
  ['anvers', mots([
    'antwerpen', 'antwerp', 'anvers', 'mechelen', 'malines', 'beerse', 'geel', 'turnhout', 'kontich', 'mortsel',
    'lier', 'herentals', 'puurs', 'boom', 'bornem', 'edegem', 'wilrijk', 'kapellen', 'schoten', 'brasschaat', 'mol',
    'westerlo', 'heist-op-den-berg', 'willebroek', 'hoogstraten', 'zoersel', 'wommelgem', 'berchem', 'deurne',
    'hoboken', 'merksem', 'ekeren', 'schelle', 'niel', 'rumst', 'duffel', 'bonheiden',
  ])],
  ['brabant-flamand', mots([
    'vlaams-brabant', 'flemish brabant', 'brabant flamand', 'leuven', 'louvain', 'asse', 'zaventem', 'vilvoorde',
    'diegem', 'machelen', 'dilbeek', 'linkebeek', 'sint-pieters-leeuw', 'halle', 'grimbergen', 'tienen', 'diest',
    'aarschot', 'groot-bijgaarden', 'wemmel', 'kraainem', 'tervuren', 'overijse', 'beersel', 'kortenberg', 'herent',
    'haacht', 'heverlee', 'kessel-lo', 'nossegem', 'zellik', 'melsbroek', 'steenokkerzeel', 'ternat', 'londerzeel',
    'keerbergen',
  ])],
  ['limbourg', mots([
    'limburg', 'limbourg', 'hasselt', 'genk', 'lommel', 'maasmechelen', 'tongeren', 'sint-truiden', 'beringen',
  ])],
  ['flandre-orientale', mots([
    'oost-vlaanderen', 'east flanders', 'flandre orientale', 'gent', 'ghent', 'gand', 'aalst', 'alost',
    'sint-niklaas', 'lebbeke', 'beveren', 'deinze', 'kruisem', 'zwijnaarde', 'merelbeke', 'ninove', 'dendermonde',
    'lokeren', 'wetteren', 'temse', 'zele', 'eeklo', 'hamme', 'oudenaarde', 'ronse', 'zottegem', 'destelbergen',
    'lochristi', 'evergem', 'aalter', 'erembodegem', 'denderleeuw', 'geraardsbergen',
  ])],
  ['flandre-occidentale', mots([
    'west-vlaanderen', 'west flanders', 'flandre occidentale', 'brugge', 'bruges', 'kortrijk', 'courtrai',
    'oostende', 'ostende', 'ostend', 'roeselare', 'zeebrugge', 'waregem', 'ieper', 'ypres', 'menen', 'harelbeke',
    'izegem', 'tielt', 'torhout', 'knokke', 'oostkamp', 'wevelgem',
  ])],
]

/** La province d'un code postal belge. */
function provinceParCode(code: number): Province | null {
  if (code >= 1300 && code <= 1499) return 'brabant-wallon'
  if (code >= 1500 && code <= 1999) return 'brabant-flamand'
  if (code >= 2000 && code <= 2999) return 'anvers'
  if (code >= 3000 && code <= 3499) return 'brabant-flamand'
  if (code >= 3500 && code <= 3999) return 'limbourg'
  if (code >= 4000 && code <= 4999) return 'liege'
  if (code >= 5000 && code <= 5999) return 'namur'
  if (code >= 6000 && code <= 6599) return 'hainaut'
  if (code >= 6600 && code <= 6999) return 'luxembourg-be'
  if (code >= 7000 && code <= 7999) return 'hainaut'
  if (code >= 8000 && code <= 8999) return 'flandre-occidentale'
  if (code >= 9000 && code <= 9999) return 'flandre-orientale'
  return null
}

/**
 * La province belge d'un lieu, ou `null`. Elle n'est cherchée que si
 * `regionDe` dit Wallonie ou Flandre, et doit lui donner raison : une
 * province qui contredirait la région serait une erreur de lecture, et
 * l'offre deviendrait invisible pour celui qui filtre sur la région.
 */
export function provinceDe(lieu: string | null | undefined): Province | null {
  const region = regionDe(lieu)
  if (region !== 'wallonie' && region !== 'flandre') return null
  const l = sansAccents(String(lieu))
  const code = l.match(/(^|\D)(\d{4})($|\D)/)
  let p: Province | null = code ? provinceParCode(Number(code[2])) : null
  if (!p) p = NOMS_PROVINCES.find(([, re]) => re.test(l))?.[0] ?? null
  return p && REGION_DE_PROVINCE[p] === region ? p : null
}

const NOMS_PAYS: [Exclude<Pays, 'autre-pays'>, RegExp][] = [
  // Les codes à trois lettres de SuccessFactors (« Hamburg, DEU, 22117 »),
  // seulement entre virgules : « che » ou « fra » au milieu d'un mot n'est pas un pays.
  ['france', /(^|[^a-z])(france|frankrijk|frankreich|paris|lyon|lille|marseille|toulouse|nantes|bordeaux|strasbourg|colombes|issy-les-moulineaux)($|[^a-z])|,\s*fra\s*(,|$)/],
  ['pays-bas', /(^|[^a-z])(netherlands|nederland|pays-bas|niederlande|holland|amsterdam|rotterdam|utrecht|eindhoven|den haag|the hague|leiden|groningen|breda|amersfoort|schiphol|zwolle)($|[^a-z])|,\s*nld\s*(,|$)/],
  ['allemagne', /(^|[^a-z])(germany|deutschland|allemagne|duitsland|berlin|munich|munchen|muenchen|frankfurt|hamburg|koln|cologne|dusseldorf|aachen|hannover|neuss|norderstedt|stuttgart|leipzig|dresden)($|[^a-z])|,\s*deu\s*(,|$)/],
  ['suisse', /(^|[^a-z])(switzerland|suisse|schweiz|zwitserland|svizzera|zurich|geneve|geneva|genf|lausanne|bern|berne|basel|bale|zug|zoug|schaffhausen|lugano)($|[^a-z])|,\s*che\s*(,|$)/],
]

/** Le pays d'un lieu étranger (`regionDe` dit `etranger`), `autre-pays` s'il
 *  n'est aucun des quatre, `null` pour un lieu qui n'est pas étranger. */
export function paysDe(lieu: string | null | undefined): Pays | null {
  if (regionDe(lieu) !== 'etranger') return null
  const l = sansAccents(String(lieu))
  return NOMS_PAYS.find(([, re]) => re.test(l))?.[0] ?? 'autre-pays'
}

/** La sous-zone d'un lieu : sa province belge, ou son pays à l'étranger. */
export function sousZoneDe(lieu: string | null | undefined): SousZone | null {
  return provinceDe(lieu) ?? paysDe(lieu)
}

// Les mots outils de chaque langue, ceux qu'aucune autre des quatre n'écrit
// pareil : ni « de », ni « en », ni « die », ni « in ». Un titre seul en dit
// rarement assez ; le début de la description suffit.
const MOTS_OUTILS: Record<LangueAnnonce, Set<string>> = {
  fr: new Set(['le', 'la', 'les', 'des', 'du', 'et', 'une', 'un', 'pour', 'avec', 'vous', 'nous', 'est', 'sont', 'dans', 'sur', 'au', 'aux', 'votre', 'notre', 'qui', 'que', 'vos', 'nos']),
  nl: new Set(['het', 'een', 'van', 'voor', 'met', 'je', 'jij', 'wij', 'zijn', 'naar', 'ons', 'onze', 'bij', 'als', 'niet', 'dat', 'aan', 'ook', 'jouw', 'wat', 'wordt']),
  en: new Set(['the', 'and', 'of', 'with', 'you', 'we', 'our', 'your', 'is', 'are', 'for', 'to', 'will', 'this', 'as', 'be']),
  de: new Set(['der', 'das', 'und', 'mit', 'für', 'wir', 'sie', 'ist', 'ein', 'eine', 'zu', 'von', 'bei', 'auf', 'nicht', 'unser', 'unsere', 'oder', 'auch', 'dem', 'den', 'ihre']),
}

/** La langue d'une annonce, devinée sur ses mots outils, ou `null` dans le
 *  doute (moins de quatre mots reconnus, ou deux langues trop proches). */
export function langueAnnonce(titre: string | null | undefined, texte: string | null | undefined): LangueAnnonce | null {
  const mots = `${titre ?? ''} ${texte ?? ''}`.toLowerCase().split(/[^a-zà-ÿß']+/)
  const n: Record<LangueAnnonce, number> = { fr: 0, nl: 0, en: 0, de: 0 }
  for (const m of mots) for (const l of LANGUES_ANNONCE) if (MOTS_OUTILS[l].has(m)) n[l]++
  const tri = [...LANGUES_ANNONCE].sort((a, b) => n[b] - n[a])
  const [premiere, seconde] = [n[tri[0]], n[tri[1]]]
  return premiere >= 4 && premiere >= 2 * seconde ? tri[0] : null
}

/** Ce que la fonction SQL passe de la description : le début suffit à la
 *  langue, et lire 15 000 descriptions entières à chaque requête ne se fait pas. */
export const LONGUEUR_LUE = 1500

/** Vrai si l'offre passe le filtre de l'abonnement. */
export function passeFiltre(
  filtre: FiltreAbonnement | null,
  lieu: string | null | undefined,
  titre?: string | null,
  texte?: string | null
): boolean {
  if (!filtre) return true
  const zones = filtre.zones ?? []
  if (zones.length > 0) {
    const region = regionDe(lieu)
    if (region !== null && !zones.includes(region)) {
      const sousZone = sousZoneDe(lieu)
      if (sousZone) {
        if (!zones.includes(sousZone)) return false
      } else if (!zones.some((z) => (SOUS_ZONES as readonly string[]).includes(z) && REGION_DE_SOUS_ZONE[z as SousZone] === region)) {
        // La province est illisible : l'offre passe si le filtre nomme une
        // province de sa région (dans le doute), pas s'il ne parle que d'autres
        // régions.
        return false
      }
    }
  }
  const langues = filtre.langues ?? []
  if (langues.length > 0) {
    const l = langueAnnonce(titre, texte)
    if (l !== null && !langues.includes(l)) return false
  }
  return true
}

/** Un filtre venu d'un corps de requête, nettoyé : zones et langues connues,
 *  sans doublon. `null` s'il ne filtre rien. */
export function normaliserFiltre(brut: unknown): FiltreAbonnement | null {
  if (!brut || typeof brut !== 'object') return null
  const o = brut as Record<string, unknown>
  const garder = <T extends string>(v: unknown, connus: readonly T[]): T[] =>
    Array.isArray(v) ? [...new Set(v.filter((x): x is T => connus.includes(x as T)))] : []
  const zones = garder(o.zones, ZONES)
  const langues = garder(o.langues, LANGUES_ANNONCE)
  if (zones.length === 0 && langues.length === 0) return null
  const f: FiltreAbonnement = {}
  if (zones.length) f.zones = zones
  if (langues.length) f.langues = langues
  return f
}

const filtresLus = new Map<string, FiltreAbonnement | null>()

/** Le filtre stocké en base (texte JSON), relu une fois par valeur distincte. */
export function lireFiltre(texte: string | null | undefined): FiltreAbonnement | null {
  if (!texte) return null
  let f = filtresLus.get(texte)
  if (f === undefined) {
    try {
      f = normaliserFiltre(JSON.parse(texte))
    } catch {
      f = null
    }
    if (filtresLus.size > 1000) filtresLus.clear()
    filtresLus.set(texte, f)
  }
  return f
}

/**
 * Enregistre `passe_filtre(filtre, lieu, titre, texte)` sur une connexion.
 * Appelée par `openDb` : une base ouverte autrement n'a pas la fonction, et
 * toute requête bornée par un abonnement y échoue avec « no such function »,
 * ce qui est voulu (bruyant plutôt qu'un filtre ignoré).
 */
export function enregistrerFonctionsFiltre(db: { function: (nom: string, opts: object, fn: (...a: any[]) => unknown) => unknown }): void {
  db.function('passe_filtre', { deterministic: true }, (filtre: string | null, lieu: string | null, titre: string | null, texte: string | null) =>
    passeFiltre(lireFiltre(filtre), lieu, titre, texte) ? 1 : 0
  )
}
